import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Box, Typography, Button, Chip, Avatar, CircularProgress } from '@mui/material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';

const API_URL = import.meta.env.VITE_API_URL || '';

const STATUS_LABELS = {
  draft: 'Wkrótce',
  scheduled: 'Wkrótce',
  registration_open: 'Zapisy otwarte',
  active: 'Trwa teraz!',
  wind_down: 'Rozliczenie',
  completed: 'Zakończona',
  paused: 'Wstrzymana',
  cancelled: 'Odwołana',
};

const DEFAULT_STEPS = [
  { title: 'Pobierz aplikację', description: 'Pobierz aplikację na telefon i zaloguj się firmowym adresem e-mail.' },
  { title: 'Dołącz do wyzwania', description: 'Wpisz kod dołączenia i potwierdź udział.' },
  { title: 'Połącz aplikacje zdrowotne', description: 'Połącz Apple Health lub Health Connect, aby Twoja aktywność liczyła się automatycznie.' },
];

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' });
}

function buildSchedule(data) {
  const rows = [];
  if (data.registration_start || data.registration_end) {
    rows.push({ label: 'Zapisy', range: `${formatDate(data.registration_start)} – ${formatDate(data.registration_end)}` });
  }
  if (data.start_date || data.end_date) {
    rows.push({ label: 'Czas trwania', range: `${formatDate(data.start_date)} – ${formatDate(data.end_date)}` });
  }
  if (data.dissolution_end) {
    rows.push({ label: 'Rozwiązanie i rozliczenie', range: `do ${formatDate(data.dissolution_end)}` });
  }
  return rows;
}

function parseJsonField(value, fallback) {
  if (!value) return fallback;
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch { return fallback; }
}

// Stylizowane odwzorowania ikon Apple Health / Health Connect (nie są to
// oryginalne pliki graficzne Apple/Google — te nie są publicznie
// dystrybuowane do swobodnego użytku), zgodne wizualnie z wersją użytą na
// generowanym plakacie kampanii.
function AppleHealthIcon({ size = 44 }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size}>
      <defs>
        <linearGradient id="landingAppleHealthGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FF7A8A" />
          <stop offset="1" stopColor="#FA233B" />
        </linearGradient>
      </defs>
      <rect width="100" height="100" rx="22" fill="white" />
      <rect width="100" height="100" rx="22" fill="none" stroke="#EDEDED" strokeWidth="2" />
      <path d="M50 82 C24 60 10 44 10 27 C10 13 21 4 34 4 C42 4 48 9 50 14 C52 9 58 4 66 4 C79 4 90 13 90 27 C90 44 76 60 50 82 Z" fill="url(#landingAppleHealthGrad)" />
      <polyline points="22,46 36,46 42,32 50,58 58,38 64,46 78,46" fill="none" stroke="white" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function HealthConnectIcon({ size = 44 }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size}>
      <defs>
        <clipPath id="landingHcLeft"><rect x="0" y="0" width="50" height="100" /></clipPath>
        <clipPath id="landingHcRight"><rect x="50" y="0" width="50" height="100" /></clipPath>
      </defs>
      <rect width="100" height="100" rx="22" fill="white" />
      <rect width="100" height="100" rx="22" fill="none" stroke="#EDEDED" strokeWidth="2" />
      <path id="landingHcHeart" d="M50 84 C22 61 8 43 8 27 C8 13 19 5 32 5 C42 5 48 12 50 18 C52 12 58 5 68 5 C81 5 92 13 92 27 C92 43 78 61 50 84 Z" />
      <use href="#landingHcHeart" fill="#00B3A4" clipPath="url(#landingHcLeft)" />
      <use href="#landingHcHeart" fill="#3D7BFF" clipPath="url(#landingHcRight)" />
    </svg>
  );
}

function CampaignLanding() {
  const theme = useTheme();
  const { code } = useParams();
  const [apiData, setApiData] = useState(null);
  const [fetchState, setFetchState] = useState('loading');

  useEffect(() => {
    setFetchState('loading');
    fetch(`${API_URL}/api/landing/${code}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => { setApiData(d); setFetchState('ok'); })
      .catch(() => setFetchState('error'));
  }, [code]);

  if (fetchState === 'loading') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (fetchState === 'error' || !apiData) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, px: 3, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={700}>Strona zaproszeniowa niedostępna</Typography>
        <Typography variant="body2" color="text.secondary">
          Ten link jest nieprawidłowy albo kampania nie została jeszcze opublikowana.
        </Typography>
      </Box>
    );
  }

  const config = parseJsonField(apiData.config, {});
  const rewardsFromCampaign = parseJsonField(apiData.rewards, []);
  const charity = parseJsonField(apiData.charity, {});

  const heroColor = config.heroColor || '#6D55F7';
  const accentColor = config.accentColor || '#9DE94B';
  const companyName = config.companyName || '';
  const campaignTitle = config.campaignTitle || apiData.title || 'Kampania';
  const subtitle = config.subtitle || '';
  const description = config.description || apiData.description || '';
  const steps = Array.isArray(config.steps) && config.steps.length ? config.steps : DEFAULT_STEPS;
  const charityEnabled = config.charityEnabled ?? !!charity.enabled;
  const charityTitle = config.charityTitle || 'Wspieramy razem';
  const charityDescription = config.charityDescription || '';
  const charityName = config.charityName || charity.name || '';
  const rules = Array.isArray(config.rules) ? config.rules : [];
  const rewards = Array.isArray(config.rewards) && config.rewards.length
    ? config.rewards
    : (Array.isArray(rewardsFromCampaign) ? rewardsFromCampaign : []);
  const requiresMobileApp = apiData.requires_mobile_app === 1 || apiData.requires_mobile_app === true;
  const coverImage = apiData.cover_image_url || null;
  const schedule = buildSchedule(apiData);
  const status = apiData.campaign_status || 'draft';
  const statusLabel = STATUS_LABELS[status] || 'Wkrótce';
  const isLive = status === 'active' || status === 'registration_open';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: heroColor, color: '#fff' }}>
      {/* ─── LIVE BANNER ───────────────────────────────────────── */}
      {isLive && (
        <Box
          sx={{
            bgcolor: accentColor,
            color: '#1a1a1a',
            py: 1.5,
            px: 3,
            textAlign: 'center',
            fontWeight: 700,
            fontSize: '0.9rem',
          }}
        >
          <Chip label={statusLabel} size="small" sx={{ bgcolor: '#fff', fontWeight: 800, color: heroColor }} />
        </Box>
      )}

      {/* ─── HERO SECTION ─────────────────────────────────────── */}
      <Box
        sx={{
          minHeight: { xs: '70vh', sm: '80vh' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 3,
          py: 8,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative shapes */}
        <Box sx={{ position: 'absolute', top: '-20%', right: '-15%', width: 600, height: 600, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: '-25%', left: '-10%', width: 500, height: 500, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />

        {companyName && (
          <Chip
            label={companyName}
            sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 700, mb: 3, fontSize: '0.875rem' }}
          />
        )}

        <Typography
          variant="h1"
          sx={{
            fontFamily: theme.typography.h1.fontFamily,
            fontWeight: 800,
            fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
            lineHeight: 1.1,
            maxWidth: 800,
            letterSpacing: '-0.03em',
            mb: 2,
            textShadow: '0 4px 30px rgba(0,0,0,0.2)',
          }}
        >
          {campaignTitle}
        </Typography>

        {subtitle && (
          <Typography variant="h5" sx={{ fontWeight: 400, opacity: 0.85, mb: 3, fontSize: { xs: '1.1rem', sm: '1.5rem' }, maxWidth: 700 }}>
            {subtitle}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            href="#jak-dolaczyc"
            sx={{
              bgcolor: accentColor,
              color: '#1a1a1a',
              fontWeight: 700,
              px: 4,
              py: 1.5,
              borderRadius: 999,
              fontSize: '1rem',
              '&:hover': { bgcolor: accentColor, filter: 'brightness(1.1)' },
            }}
          >
            Dołącz do wyzwania
          </Button>
        </Box>
      </Box>

      {/* ─── COVER IMAGE ──────────────────────────────────────── */}
      {coverImage && (
        <Box sx={{ width: '100%', maxHeight: 420, overflow: 'hidden' }}>
          <Box
            component="img"
            src={coverImage}
            alt={campaignTitle}
            sx={{ width: '100%', height: { xs: 220, sm: 320, md: 420 }, objectFit: 'cover', display: 'block' }}
          />
        </Box>
      )}

      {/* ─── DESCRIPTION + SCHEDULE ───────────────────────────── */}
      {(description || schedule.length > 0) && (
        <Box sx={{ bgcolor: '#fff', color: 'text.primary', py: { xs: 6, sm: 10 }, px: 3 }}>
          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            {description && (
              <Typography variant="body1" sx={{ fontSize: '1.125rem', lineHeight: 1.8, color: 'text.secondary', maxWidth: 700, mb: schedule.length ? 5 : 0 }}>
                {description}
              </Typography>
            )}
            {schedule.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                }}
              >
                {schedule.map((s, idx) => (
                  <Box
                    key={s.label}
                    sx={{
                      flex: 1,
                      p: 3,
                      borderRight: { sm: idx < schedule.length - 1 ? '1px solid' : 'none' },
                      borderBottom: { xs: idx < schedule.length - 1 ? '1px solid' : 'none', sm: 'none' },
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: heroColor }}>
                      {s.label}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.5 }}>
                      {s.range}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* ─── STEPS + CODE/QR SECTION ──────────────────────────── */}
      <Box id="jak-dolaczyc" sx={{ bgcolor: heroColor, py: { xs: 6, sm: 10 }, px: 3 }}>
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <Typography
            variant="h3"
            sx={{
              fontFamily: theme.typography.h1.fontFamily,
              fontWeight: 750,
              fontSize: { xs: '1.75rem', sm: '2.5rem' },
              mb: 5,
              textAlign: 'center',
            }}
          >
            Jak dołączyć?
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {steps.map((step, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
                <Avatar
                  sx={{
                    bgcolor: accentColor,
                    color: '#1a1a1a',
                    fontWeight: 800,
                    fontSize: '1.5rem',
                    width: 56,
                    height: 56,
                    fontFamily: theme.typography.h1.fontFamily,
                    flexShrink: 0,
                  }}
                >
                  {idx + 1}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.85, lineHeight: 1.6 }}>
                    {step.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Health integrations */}
          {requiresMobileApp && (
            <Box sx={{ mt: 5, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 999, pr: 3, pl: 1, py: 1 }}>
                <AppleHealthIcon size={40} />
                <Typography sx={{ fontWeight: 700 }}>Apple Health</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 999, pr: 3, pl: 1, py: 1 }}>
                <HealthConnectIcon size={40} />
                <Typography sx={{ fontWeight: 700 }}>Health Connect</Typography>
              </Box>
            </Box>
          )}

          {/* Code + QR */}
          {apiData.code && (
            <Box
              sx={{
                mt: 5,
                p: 4,
                borderRadius: `${theme.tokens?.primitive?.radius?.hero ?? 30}px`,
                bgcolor: 'rgba(255,255,255,0.1)',
                textAlign: 'center',
              }}
            >
              <QrCode2Icon sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="body1" sx={{ mb: 1, opacity: 0.8 }}>
                Pobierz aplikację i wpisz kod dołączenia
              </Typography>
              <Typography variant="h5" sx={{ fontFamily: theme.typography.h1.fontFamily, fontWeight: 800 }}>
                KOD: {apiData.code}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* ─── CHARITY SECTION ──────────────────────────────────── */}
      {charityEnabled && (charityDescription || charityName) && (
        <Box sx={{ bgcolor: '#fff', color: 'text.primary', py: { xs: 6, sm: 10 }, px: 3 }}>
          <Box sx={{ maxWidth: 900, mx: 'auto', textAlign: 'center' }}>
            <FavoriteBorderIcon sx={{ fontSize: 48, color: heroColor, mb: 2 }} />
            <Typography
              variant="h3"
              sx={{
                fontFamily: theme.typography.h1.fontFamily,
                fontWeight: 750,
                fontSize: { xs: '1.75rem', sm: '2.5rem' },
                color: heroColor,
                mb: 2,
              }}
            >
              {charityTitle}
            </Typography>
            {charityDescription && (
              <Typography variant="body1" sx={{ fontSize: '1.125rem', lineHeight: 1.8, color: 'text.secondary', mb: 2, maxWidth: 650, mx: 'auto' }}>
                {charityDescription}
              </Typography>
            )}
            {charityName && <Chip label={charityName} sx={{ fontWeight: 700, fontSize: '1rem', py: 2.5, px: 1 }} />}
          </Box>
        </Box>
      )}

      {/* ─── RULES SECTION ─────────────────────────────────────── */}
      {rules.length > 0 && (
        <Box sx={{ bgcolor: accentColor, color: '#1a1a1a', py: { xs: 6, sm: 10 }, px: 3 }}>
          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: theme.typography.h1.fontFamily,
                fontWeight: 750,
                fontSize: { xs: '1.75rem', sm: '2.5rem' },
                mb: 5,
              }}
            >
              Zasady gry
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
              {rules.map((rule, idx) => (
                <Box
                  key={idx}
                  sx={{ p: 3, borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`, bgcolor: 'rgba(255,255,255,0.6)' }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {rule.title}
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    {rule.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {/* ─── REWARDS SECTION ──────────────────────────────────── */}
      {rewards.length > 0 && (
        <Box sx={{ bgcolor: '#fff', color: 'text.primary', py: { xs: 6, sm: 10 }, px: 3 }}>
          <Box sx={{ maxWidth: 900, mx: 'auto', textAlign: 'center' }}>
            <EmojiEventsIcon sx={{ fontSize: 48, color: heroColor, mb: 2 }} />
            <Typography
              variant="h3"
              sx={{
                fontFamily: theme.typography.h1.fontFamily,
                fontWeight: 750,
                fontSize: { xs: '1.75rem', sm: '2.5rem' },
                color: heroColor,
                mb: 4,
              }}
            >
              Nagrody
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
              {rewards.map((reward, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 3,
                    borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`,
                    border: '2px solid',
                    borderColor: heroColor,
                    minWidth: 200,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h4" sx={{ fontFamily: theme.typography.h1.fontFamily, fontWeight: 800, color: heroColor }}>
                    #{reward.place}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                    {reward.prize}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {/* ─── FOOTER CTA ───────────────────────────────────────── */}
      <Box sx={{ bgcolor: '#1a1a1a', color: '#fff', py: { xs: 6, sm: 10 }, px: 3, textAlign: 'center' }}>
        <Typography
          variant="h2"
          sx={{
            fontFamily: theme.typography.h1.fontFamily,
            fontWeight: 800,
            fontSize: { xs: '2rem', sm: '3.5rem' },
            mb: 2,
          }}
        >
          Dołącz już dziś!
        </Typography>
        {apiData.code && (
          <Button
            variant="contained"
            size="large"
            href="#jak-dolaczyc"
            sx={{
              bgcolor: accentColor,
              color: '#1a1a1a',
              fontWeight: 700,
              px: 5,
              py: 1.5,
              borderRadius: 999,
              fontSize: '1.1rem',
              '&:hover': { bgcolor: accentColor, filter: 'brightness(1.1)' },
            }}
          >
            Dołącz teraz
          </Button>
        )}
        <Typography variant="caption" sx={{ display: 'block', mt: 3, opacity: 0.4 }}>
          Powered by Engagement Challenges
        </Typography>
      </Box>
    </Box>
  );
}

export default CampaignLanding;
